import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { Map, MapRef } from "react-map-gl/dist/maplibre"
import { StyleSpecification } from "maplibre-gl"
import { mapThemes } from "features/map/map-themes/mapThemes"
import VideoLayer, { VideoLayerProps } from "features/map/layers/video/Video.class"
import { VideoMetadata } from "@nova/slice-video"
import { droneIconColors } from "features/map/layers/video/VideoDroneLayer"
import { DeckGLOverlay } from "features/map/MapPreview"
import { FlightPathPoint } from "@nova/schemas"
import { useLocalStorageValue } from "tools/store/localStorageRegistry"

const POPUP_WIDTH = 280
const POPUP_HEIGHT = 200

interface Props {
    previewAnchor: { anchorRect: DOMRect; missionId: string } | null
    points: FlightPathPoint[] | null
}

export function FlightPathPreviewPopup({ previewAnchor, points }: Props) {
    const [visible, setVisible] = useState(false)
    const mapRef = useRef<MapRef | null>(null)

    const hasPoints = !!points && points.length > 0
    const shouldShow = !!previewAnchor && hasPoints

    useEffect(() => {
        if (shouldShow) {
            const t = setTimeout(() => setVisible(true), 175)
            return () => clearTimeout(t)
        } else {
            setVisible(false)
        }
    }, [shouldShow])

    const mapBackground = useLocalStorageValue("mapBackground")
    const mapStylesJSON = mapThemes[mapBackground]

    const activeMissionId = previewAnchor?.missionId ?? "preview"

    const activePoints = useMemo(() => points ?? [], [points])
    const anchorRect = previewAnchor?.anchorRect

    const coords = useMemo(
        (): VideoMetadata =>
            activePoints.map((p) => [
                Number(p.longitude),
                Number(p.latitude),
                p.altitude ?? 0,
                p.heading == null ? NaN : 0,
                p.heading ?? NaN,
                p.heading == null ? NaN : 0,
                0,
                0,
            ]),
        [activePoints]
    )

    const layers = useMemo(() => {
        if (!coords.length) return []
        const droneCoord = coords[coords.length - 1]
        const shared = {
            flightId: activeMissionId,
            frameNumber: 1,
            mediaType: "image",
            frameEnabled: false,
            crosshairEnabled: false,
            live: true,
            color: droneIconColors[0],
            getVideoElement: () => null,
        } satisfies Partial<VideoLayerProps>
        return [
            new VideoLayer({
                ...shared,
                id: `preview-path-${activeMissionId}`,
                data: coords,
                flightPathEnabled: true,
                droneEnabled: false,
            }),
            new VideoLayer({
                ...shared,
                id: `preview-drone-${activeMissionId}`,
                data: [droneCoord],
                flightPathEnabled: false,
                droneEnabled: true,
            }),
        ]
    }, [coords, activeMissionId])

    const initialBounds = useMemo(() => {
        if (!coords.length) return undefined
        const lngs = coords.map((c) => c[0])
        const lats = coords.map((c) => c[1])
        return [
            [Math.min(...lngs), Math.min(...lats)],
            [Math.max(...lngs), Math.max(...lats)],
        ] as unknown as [number, number, number, number]
    }, [coords])

    const updateMapBounds = useCallback(() => {
        if (!mapRef.current || !coords.length) return
        const lngs = coords.map((c) => c[0])
        const lats = coords.map((c) => c[1])
        try {
            mapRef.current.fitBounds(
                [
                    [Math.min(...lngs), Math.min(...lats)],
                    [Math.max(...lngs), Math.max(...lats)],
                ],
                { padding: 20, animate: false, maxZoom: 14 }
            )
        } catch {}
    }, [coords])

    useEffect(() => {
        updateMapBounds()
    }, [updateMapBounds])

    const portal = document.getElementById("portal")
    if (!portal || !anchorRect) return null

    const fitsBelow = anchorRect.bottom + 8 + POPUP_HEIGHT <= window.innerHeight
    const top = fitsBelow ? anchorRect.bottom : anchorRect.top - POPUP_HEIGHT
    const left = Math.min(
        Math.max(8, anchorRect.left + anchorRect.width / 2 - POPUP_WIDTH / 2),
        window.innerWidth - POPUP_WIDTH - 8
    )

    return createPortal(
        <div
            className={`fixed z-50 transition-opacity pointer-events-none duration-100 bg-gray-800 ${visible ? "opacity-100" : "opacity-0"}`}
            style={{ top, left, width: POPUP_WIDTH, height: POPUP_HEIGHT }}
        >
            <Map
                ref={mapRef}
                mapStyle={mapStylesJSON as StyleSpecification}
                initialViewState={{
                    bounds: initialBounds,
                    fitBoundsOptions: { padding: 20, maxZoom: 18 },
                }}
                attributionControl={false}
                style={{ width: "100%", height: "100%", borderRadius: "8px" }}
            >
                <DeckGLOverlay layers={layers} />
            </Map>
        </div>,
        portal
    )
}
